/*!

=========================================================
* Light Bootstrap Dashboard React - v2.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/light-bootstrap-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, {Suspense, useCallback, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom/client";

import { BrowserRouter, Route, Switch, Redirect, useLocation } from "react-router-dom";

import "./assets/scss/light-bootstrap-dashboard-react.scss";
import "./assets/scss/ta-theme.scss";

import DashboardLayout from "./layouts/Dashboard.js";
import AuthLayout from "layouts/Auth.js";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { persistor, store } from "./utils/Store";
import { PersistGate } from "redux-persist/integration/react";
import { Provider, useDispatch, useSelector } from "react-redux";
import * as StorageManager from "./utils/StorageManager";
import constants from "../src/constants";
import _ from "lodash";
import NotFound from "./views/404";
import Faq from "views/Faq";
import * as authSelectors from "./selectors/auth";
import * as axiosInstance from "./services/axiosInstance";
import * as userActions from "./action/user";
import Helper from "./utils/Helper";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const queryClient = new QueryClient({retry:true});

if (!process.env.REACT_APP_DEBUG) {
  console.log = () => {}
  console.error = () => {}
  console.debug = () => {}
}

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <BrowserRouter>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <App></App>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  </BrowserRouter>,
);
function App(props) {
  const [progress, setProgress] = useState(false);
  const [prevLoc, setPrevLoc] = useState("");
  const [token, setToken] = useState(null);
  const isLogout = useSelector((state) => authSelectors.getAuthIsLogout(state));
  let location = useLocation();
  const dispatch = useDispatch();
  useEffect(() => {
    setTokenFunction();
  }, [isLogout]);

  async function setTokenFunction() {
    setToken(await StorageManager.retrieveToken(constants.AUTH_TOKEN));
  }

  const { data, isLoading, isFetched, isFetching, isSuccess } = useQuery({
    queryKey: ["profileData"],
    queryFn: async () => {
      try {
        return await axiosInstance.get("clientarea/profile", dispatch);
      } catch (e) {
        Helper.handleHttpError(e, dispatch);
      }
    },
    retryDelay: 15000,
    enabled: !_.isEmpty(token),
  });

  useEffect(() => {
    if (isSuccess) {
      dispatch(userActions.updateUserDetail(data.data));
    }
  }, [isSuccess]);


  useEffect(() => {
    setPrevLoc(location.pathname);
    setProgress(true);
    if (location.pathname === prevLoc) {
      setPrevLoc("");
    }
  }, [location]);

  useEffect(() => {
    setProgress(false);
  }, [prevLoc]);

  // const token = StorageManager.retrieveToken(constants.AUTH_TOKEN);
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <ToastContainer />
          <Switch>
            <Route
              exact
              path="/"
              render={() => {
                return _.isEmpty(token) ? <Redirect to="/auth/login" /> : <Redirect to="/dashboard" />;
              }}
            />
            <Route path="/dashboard/" render={(props) => <DashboardLayout {...props}  />} />
            <Route path="/auth" render={(props) => <AuthLayout {...props} />} />
            <Route path="/static/help" component={Faq} />
            <Route path={`*`} component={NotFound} key="not-found" />{" "}
          </Switch>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
}
